import { FC, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { Location } from 'types/location';
import {
  ContractMigrationMode,
  ContractMigrationResponse,
  downloadContractMigrationTemplate,
  uploadContractMigration
} from 'services/contracts';
import ErrorList from 'pages/admin/migration/components/error-list';

interface ContractMigrationDialogProps {
  open: boolean;
  location: Location | null;
  onClose: () => void;
  onSuccess: () => void;
}

const MODE_DESCRIPTIONS: Record<ContractMigrationMode, string> = {
  UPSERT:
    'Updates contracts that match an existing customer, size type, and start date, adds new rows, and truncates any overlapping existing schedules so the uploaded rates apply. Overlaps between rows in the same file are rejected.',
  REPLACE:
    'Deletes ALL contracts for the selected location and imports the workbook. This is destructive and cannot be undone.'
};

const ContractMigrationDialog: FC<ContractMigrationDialogProps> = ({ open, location, onClose, onSuccess }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<ContractMigrationMode | null>(null);
  const [confirmingReplace, setConfirmingReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ContractMigrationResponse | null>(null);
  const [transportError, setTransportError] = useState<string | null>(null);

  const canSubmit = !!location && !!file && !!mode && !uploading;

  const reset = () => {
    setFile(null);
    setMode(null);
    setConfirmingReplace(false);
    setResult(null);
    setTransportError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const submit = async () => {
    if (!location || !file || !mode || uploading) return;
    setUploading(true);
    setTransportError(null);
    try {
      const response = await uploadContractMigration(location.id, mode, file);
      setResult(response);
      if (response.recordsFailed === 0) {
        onSuccess();
      }
    } catch (e: any) {
      setTransportError(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handlePrimaryAction = () => {
    if (!mode) return;
    if (mode === 'REPLACE' && !confirmingReplace) {
      setConfirmingReplace(true);
      return;
    }
    submit();
  };

  const renderSummary = () => {
    if (!result) return null;
    return (
      <Box mt={2}>
        {result.recordsFailed > 0 ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {result.recordsFailed} of {result.recordsRead} row{result.recordsFailed !== 1 ? 's' : ''} failed. No changes
            were written.
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mb: 1 }}>
            Imported {result.recordsImported} of {result.recordsRead} rows ({result.mode}).
          </Alert>
        )}
        {result.errors && result.errors.length > 0 && <ErrorList errors={result.errors} />}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Contract Migration</DialogTitle>
      <DialogContent dividers>
        {location ? (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Location: <strong>{location.name}</strong> ({location.code})
          </Typography>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No location selected. Select a location before migrating contracts.
          </Alert>
        )}

        <Button variant="outlined" size="small" onClick={downloadContractMigrationTemplate} sx={{ mb: 2 }}>
          Download Template (.xlsx)
        </Button>

        {confirmingReplace && mode === 'REPLACE' ? (
          <Alert severity="error">
            This will delete all contracts for <strong>{location?.name}</strong> ({location?.code}) and replace them
            with the uploaded workbook. This cannot be undone. Continue?
          </Alert>
        ) : (
          <>
            <Box mb={2}>
              <input
                ref={fileInputRef}
                data-testid="contract-migration-file-input"
                type="file"
                accept=".xlsx"
                onChange={e => {
                  setFile(e.target.files?.[0] ?? null);
                  setResult(null);
                  setTransportError(null);
                }}
              />
            </Box>

            <RadioGroup
              value={mode ?? ''}
              onChange={e => {
                setMode(e.target.value as ContractMigrationMode);
                setConfirmingReplace(false);
                setResult(null);
              }}
            >
              <FormControlLabel value="UPSERT" control={<Radio />} label="Upsert" disabled={uploading} />
              <Typography variant="caption" color="text.secondary" display="block" pl={3} mb={1}>
                {MODE_DESCRIPTIONS.UPSERT}
              </Typography>
              <FormControlLabel
                value="REPLACE"
                control={<Radio />}
                label="Replace all for location (truncate)"
                disabled={uploading}
              />
              <Typography variant="caption" color="text.secondary" display="block" pl={3} mb={1}>
                {MODE_DESCRIPTIONS.REPLACE}
              </Typography>
            </RadioGroup>
          </>
        )}

        {transportError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {transportError}
          </Alert>
        )}
        {renderSummary()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Close
        </Button>
        {confirmingReplace && mode === 'REPLACE' ? (
          <>
            <Button onClick={() => setConfirmingReplace(false)} disabled={uploading}>
              Back
            </Button>
            <Button color="error" variant="contained" onClick={submit} disabled={!file || uploading}>
              {uploading ? 'Uploading...' : 'Confirm Replace'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handlePrimaryAction}
            disabled={!canSubmit}
            color={mode === 'REPLACE' ? 'error' : 'primary'}
          >
            {mode === 'REPLACE' ? 'Replace All...' : 'Upload'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContractMigrationDialog;
